/**
 * useBuilderRoomControls — room-side glue for the multi-builder control surface (Step 4).
 *
 * Owns the pieces the room needs to render the locked design over the reducer's run-level
 * ``pendingBuilderWaits`` + ``agentRoster``:
 *   - the "N awaiting your response" count (above the prompt),
 *   - which awaiting wait is currently SELECTED (its band opens above the prompt, one at a time),
 *   - a per-row Stop handler for RUNNING builders (targets that row's runId),
 *   - the locally-handled-wait set so a just-answered band dismisses before the RESOLVED echo.
 *
 * All the branchy derivation lives in the pure ``deriveBuilderControls`` (ui-shared, unit-tested);
 * this hook adds React state + the cancel/select callbacks. The room passes ``renderRowAction`` to
 * AgentHierarchyPanel and ``selectedWait`` to the Composer's builder band.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { deriveBuilderControls, waitRunIdForAgent } from '@stepflow/ui-shared/utils/builderRoomControls.js';
import { cancelUniversalBuilderRun } from '@stepflow/ui-shared/services/builderCliApi';

export default function useBuilderRoomControls({
  pendingBuilderWaits = [],
  agentRoster = [],
  networkId = null,
  poolId = null,
  canRespond = true,
}) {
  // Waits the local user has answered/cancelled this session (optimistic dismiss).
  const [handledWaitEventIds, setHandledWaitEventIds] = useState([]);
  const markWaitHandled = useCallback((waitEventId) => {
    if (!waitEventId) return;
    setHandledWaitEventIds((prev) => (prev.includes(waitEventId) ? prev : [...prev, waitEventId]));
  }, []);

  // Which awaiting wait's band is open (by runId). Null = none (user handles one at a time).
  const [selectedWaitRunId, setSelectedWaitRunId] = useState(null);

  // Per-builder Stop tracking (which runIds are mid-cancel) so each row shows its own spinner.
  const [stoppingRunIds, setStoppingRunIds] = useState([]);
  const stoppingSet = useMemo(() => new Set(stoppingRunIds), [stoppingRunIds]);

  const controls = useMemo(
    () => deriveBuilderControls({
      pendingBuilderWaits, agentRoster, handledWaitEventIds, selectedWaitRunId,
    }),
    [pendingBuilderWaits, agentRoster, handledWaitEventIds, selectedWaitRunId],
  );

  // If the selected wait disappears (resolved by us or a peer, or the run ended), drop the
  // selection so the band closes and the next click can open a different one.
  useEffect(() => {
    if (selectedWaitRunId && !controls.selectedWait) setSelectedWaitRunId(null);
  }, [selectedWaitRunId, controls.selectedWait]);

  /** Select the awaiting builder on a clicked roster row → opens its band above the prompt. */
  const respondToAgent = useCallback((agentId) => {
    const runId = waitRunIdForAgent(pendingBuilderWaits, agentId);
    if (runId) setSelectedWaitRunId(runId);
  }, [pendingBuilderWaits]);

  /** Stop a RUNNING builder by its runId (targeted — the user picked its row). */
  const stopBuilder = useCallback(async (runId) => {
    if (!runId || !networkId || stoppingSet.has(runId)) return;
    setStoppingRunIds((prev) => [...prev, runId]);
    try {
      await cancelUniversalBuilderRun(networkId, { runId, poolId });
    } catch (e) {
      // Non-fatal — the server-side CodeBuild fallback may still fire. Surface for diagnosis.
      console.error('[builder-controls] stop failed:', e);
    } finally {
      setStoppingRunIds((prev) => prev.filter((r) => r !== runId));
    }
  }, [networkId, poolId, stoppingSet]);

  /** Mark the selected wait handled + close its band (called on band send/cancel). */
  const onSelectedWaitHandled = useCallback(() => {
    const w = controls.selectedWait;
    if (w && w.waitEventId) markWaitHandled(w.waitEventId);
    setSelectedWaitRunId(null);
  }, [controls.selectedWait, markWaitHandled]);

  return {
    // Above-prompt notice + band.
    awaitingCount: controls.awaitingCount,
    awaitingWaits: controls.awaitingWaits,
    selectedWait: controls.selectedWait,
    onSelectedWaitHandled,
    // Per-row action inputs.
    runningBuilderRunIds: controls.runningBuilderRunIds,
    respondToAgent,
    stopBuilder,
    stoppingSet,
    selectedWaitRunId,
    setSelectedWaitRunId,
    canRespond,
    markWaitHandled,
  };
}
